import { useState, useEffect } from "react";
import axios from "axios";

const useLoginAttempt = (email) => {
  const [status, setStatus] = useState({
    attemptCount: 0,
    isLocked: false,
    lockUntil: null,
    remainingTime: null,
    attemptsRemaining: 3,
  });
  const [loading, setLoading] = useState(false);

  // Fetch login status from API
  const fetchStatus = async (email) => {
    if (!email || !isValidEmail(email)) return;

    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/public/login-status-by-email`;

      const res = await axios.post(
        url,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 5000, // 5 second timeout
        },
      );

      // Check response structure
      // NOTE: axios interceptor unwraps response.data, so res is already the data object
      if (res && res.success && res.data) {
        // Standard wrapped response: {success: true, data: {...}}
        setStatus(res.data);
      } else if (res && typeof res === "object" && "attemptCount" in res) {
        // Direct response object (when interceptor already unwrapped)
        setStatus(res);
      } else if (res && res.success === false) {
        // API responded with error (user not found, etc.) - keep default state
        setStatus({
          attemptCount: 0,
          isLocked: false,
          lockUntil: null,
          remainingTime: null,
          attemptsRemaining: 3,
        });
      }
    } catch (error) {
      // Silently handle errors - login status check is optional
      // On error, reset to default state (no lock, no attempts)
      // This allows login to proceed even if status check fails
      setStatus({
        attemptCount: 0,
        isLocked: false,
        lockUntil: null,
        remainingTime: null,
        attemptsRemaining: 3,
      });
    } finally {
      setLoading(false);
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Debounced fetch on email change
  useEffect(() => {
    if (!email || !isValidEmail(email)) {
      setStatus({
        attemptCount: 0,
        isLocked: false,
        lockUntil: null,
        remainingTime: null,
        attemptsRemaining: 3,
      });
      return;
    }

    const timeout = setTimeout(() => {
      fetchStatus(email);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeout);
  }, [email]);

  // Countdown timer for locked accounts
  useEffect(() => {
    if (
      !status.isLocked ||
      !status.remainingTime ||
      status.remainingTime <= 0
    ) {
      return;
    }

    const interval = setInterval(() => {
      setStatus((prev) => {
        const newRemainingTime = Math.max(0, prev.remainingTime - 1000);

        if (newRemainingTime === 0) {
          return {
            ...prev,
            isLocked: false,
            lockUntil: null,
            remainingTime: null,
            attemptCount: 0,
            attemptsRemaining: 3,
          };
        }

        return {
          ...prev,
          remainingTime: newRemainingTime,
        };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status.isLocked, status.remainingTime]);

  // Format remaining time
  const formatTime = (ms) => {
    if (!ms || ms <= 0) return "0m 0s";

    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  };

  return {
    isLocked: status.isLocked,
    attemptCount: status.attemptCount,
    attemptsRemaining: status.attemptsRemaining,
    remainingTime: status.remainingTime,
    formattedTime: formatTime(status.remainingTime),
    loading,
    refetch: () => fetchStatus(email),
  };
};

export default useLoginAttempt;
